import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Container, CustomInput, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { KIND_LABELS, addWeeks, euros, mondayOf, parseEuros, weekLabel } from "../../helpers/displaySpaces";
import { GuideLinks } from "./GuideLinks";

// Planning des espaces partenaires : grille espaces × semaines ; prix forcé, remise coupée, blocage interne,
// et saisie des réservations des contrats cadres (sans devis).
const WEEKS = 12;

const CELL_STYLE = {
    free: { background: "#fff" },
    past: { background: "#f3f4f6", color: "#9ca3af" },
    held: { background: "#fef3c7" },
    booked: { background: "#dcfce7" },
    blocked: { background: "#e5e7eb" },
};

// othersText : partenaires des places suivantes de la semaine (espace à plusieurs envois).
function othersText(c) {
    return (c.others || []).map((o) => `+ ${o.partnerName}${o.status === "held" ? " (en attente)" : ""}`).join(" ");
}

function cellText(c) {
    switch (c.status) {
        case "booked": return c.origin === "framework" ? `Contrat — ${c.partnerName}` : `Réservé — ${c.partnerName}`;
        case "held": return `En attente — ${c.partnerName}`;
        case "blocked": return "Bloqué (interne)";
        case "past": return "—";
        default: return null;
    }
}

export function Planning() {
    const [from, setFrom] = useState(mondayOf());
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [cell, setCell] = useState(null); // {space, c, priceText, discountDisabled, blocked, note}
    const [frame, setFrame] = useState(null);
    const [partners, setPartners] = useState([]);
    const [busy, setBusy] = useState(false);
    const [season, setSeason] = useState(null);
    const [seasonCells, setSeasonCells] = useState([]); // {week, mode: default|high|low, label}

    const load = () => {
        setRows(null);
        Promise.all([
            apiFetch(`${API.back}/display-planning?from=${from}&weeks=${WEEKS}`, { market: false }),
            apiFetch(`${API.back}/display-season?from=${from}&weeks=${WEEKS}`, { market: false }),
        ])
            .then(async ([res, sres]) => {
                if (!res.ok) throw new Error(await readError(res));
                setSeasonCells(sres.ok ? await sres.json() : []);
                return res.json();
            })
            .then(setRows)
            .catch((e) => { setError("Planning indisponible : " + e.message); setRows([]); });
    };
    useEffect(load, [from]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        apiFetch(API.back + "/partner-report-configs", { market: false }).then((r) => (r.ok ? r.json() : [])).then((d) => setPartners(Array.isArray(d) ? d : [])).catch(() => setPartners([]));
    }, []);

    const openCell = (space, c) => {
        if (c.status === "past" || c.status === "held" || c.status === "booked") return;
        setError(null);
        setCell({ space, c, priceText: c.priceOverride != null ? euros(c.priceOverride, false) : "", discountDisabled: !!c.discountDisabled, blocked: c.status === "blocked", note: c.note || "" });
    };

    const saveCell = async () => {
        const forced = cell.priceText.trim() === "" ? null : parseEuros(cell.priceText);
        if (cell.priceText.trim() !== "" && forced === null) { setError("Prix invalide : saisis un montant en euros, ex. 900 ou 900,50 (vide = prix de la grille)."); return; }
        setBusy(true);
        const body = { spaceId: cell.space.id, week: cell.c.week, discountDisabled: cell.discountDisabled, note: cell.note, priceOverride: forced };
        const wasBlocked = cell.c.status === "blocked";
        if (cell.blocked !== wasBlocked) body.blocked = cell.blocked;
        const res = await apiFetch(API.back + "/display-slot", { method: "PUT", market: false, body: JSON.stringify(body) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setCell(null);
        load();
    };

    const openFrame = () => {
        setError(null);
        setFrame({ spaceId: rows?.[0]?.space.id || "", partner: "", mode: "quote", contractRef: "", free: false, quantity: 1, from: addWeeks(mondayOf(), 1), to: addWeeks(mondayOf(), 4), result: null });
    };

    const saveFrame = async () => {
        const space = rows.find((r) => r.space.id === frame.spaceId)?.space;
        const p = partners.find((x) => `${x.merchantKey}#${x.market}` === frame.partner);
        if (!space || !p) { setError("Choisis un espace et un partenaire."); return; }
        if (frame.to < frame.from) { setError("La fin de période précède son début."); return; }
        setBusy(true);
        setError(null);
        const body = { spaceId: space.id, partnerKey: p.merchantKey, partnerName: p.displayName || p.merchantKey, mode: frame.mode,
            contractRef: frame.mode !== "quote" ? frame.contractRef : "", free: frame.mode === "direct" && !!frame.free, from: frame.from, to: frame.to,
            quantity: space.unit ? Number(frame.quantity) || 1 : 0 };
        const res = await apiFetch(API.back + "/display-framework", { method: "POST", market: false, body: JSON.stringify(body) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setFrame({ ...frame, result: await res.json() });
        load();
    };

    const openSeason = (c) => {
        setError(null);
        setSeason({ week: c.week, mode: c.seasonDefault ? "default" : c.high ? "high" : "low", label: c.label || "" });
    };
    const saveSeason = async () => {
        setBusy(true);
        const body = { week: season.week, label: season.label.trim() };
        if (season.mode !== "default") body.high = season.mode === "high";
        const res = await apiFetch(API.back + "/display-season", { method: "PUT", market: false, body: JSON.stringify(body) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setSeason(null);
        load();
    };

    const frameSpace = frame && rows?.find((r) => r.space.id === frame.spaceId)?.space;
    const framePartners = partners.filter((p) => p.enabled !== false && (!frameSpace || p.market === frameSpace.market));

    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h3 mb-0">Planning des espaces</h1>
                <div className="d-flex align-items-center">
                    <Button outline className="mr-1" onClick={() => setFrom(addWeeks(from, -WEEKS))}>‹</Button>
                    <Button outline className="mr-1" onClick={() => setFrom(mondayOf())}>Cette semaine</Button>
                    <Button outline className="mr-3" onClick={() => setFrom(addWeeks(from, WEEKS))}>›</Button>
                    <Button color="primary" onClick={openFrame} disabled={!rows || rows.length === 0}>Réserver pour un partenaire</Button>
                </div>
            </div>
            <GuideLinks />
            {error && !cell && !frame && <Alert color="danger">{error}</Alert>}
            <Card>
                <CardBody style={{ overflowX: "auto" }}>
                    {rows === null ? <Spinner /> : rows.length === 0 ? <p className="mb-0 text-muted">Crée d'abord un espace dans « Espaces ».</p> : (
                        <Table bordered size="sm" className="mb-0" style={{ minWidth: 1100, fontSize: 12 }}>
                            <thead>
                                <tr><th style={{ minWidth: 180 }}>Espace</th>{rows[0].cells.map((c) => <th key={c.week} className="text-center">{weekLabel(c.week)}</th>)}</tr>
                                <tr>
                                    <th className="font-weight-normal text-muted">Saison <small>(clic pour régler)</small></th>
                                    {(seasonCells.length === rows[0].cells.length ? seasonCells : rows[0].cells.map((c) => ({ week: c.week, high: c.high, label: c.label, default: true }))).map((c) => (
                                        <th key={c.week} className="text-center font-weight-normal" style={{ cursor: "pointer", background: c.high ? "#fef9c3" : "#f8fafc" }}
                                            onClick={() => openSeason({ week: c.week, high: c.high, label: c.label, seasonDefault: c.default })} title={c.label || ""}>
                                            {c.high ? "Haute" : "Basse"}{c.label ? <div style={{ fontSize: 10, color: "#2563eb" }}>{c.label}</div> : null}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.space.id} className={r.space.active ? "" : "text-muted"}>
                                        <td><strong>{r.space.name?.fr || r.space.name?.[r.space.market]}</strong><br />{KIND_LABELS[r.space.kind]} · {r.space.market.toUpperCase()}</td>
                                        {r.cells.map((c) => (
                                            <td key={c.week} className="text-center" style={{ ...CELL_STYLE[c.status], cursor: ["free", "blocked"].includes(c.status) ? "pointer" : "default" }}
                                                onClick={() => openCell(r.space, c)} title={c.note || ""}>
                                                {othersText(c) && <div className="small font-weight-bold">{othersText(c)}</div>}
                                                {cellText(c) || (
                                                    <>
                                                        {c.discountPct > 0 && <div style={{ textDecoration: "line-through", color: "#9ca3af" }}>{euros(c.listPrice)}</div>}
                                                        <div>{euros(c.price)}{c.priceOverride != null ? " *" : ""}</div>
                                                    </>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <small className="text-muted d-block mt-2">Prix HT (par unité pour un espace à l'unité). * = prix forcé. Clic sur une semaine libre ou bloquée pour la régler. Haute saison par défaut : janvier, juin, novembre ; les temps forts se règlent sur la ligne « Saison ».</small>
                </CardBody>
            </Card>

            <Modal isOpen={!!cell} toggle={() => setCell(null)}>
                <ModalHeader toggle={() => setCell(null)}>{cell && `${cell.space.name?.fr || ""} — semaine du ${weekLabel(cell.c.week)}`}</ModalHeader>
                {cell && (
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        <FormGroup><Label>Prix forcé (€ HT, vide = grille {euros(cell.c.high && cell.space.highSeasonPrice ? cell.space.highSeasonPrice : cell.space.weeklyPrice)}{cell.c.high ? " haute saison" : ""})</Label>
                            <Input value={cell.priceText} onChange={(e) => setCell({ ...cell, priceText: e.target.value })} /></FormGroup>
                        <CustomInput type="switch" id="cell-disc" checked={cell.discountDisabled} onChange={(e) => setCell({ ...cell, discountDisabled: e.target.checked })} label="Couper la remise de dernière minute sur cette semaine" />
                        <CustomInput className="mt-2" type="switch" id="cell-block" checked={cell.blocked} onChange={(e) => setCell({ ...cell, blocked: e.target.checked })} label="Bloquer la semaine (usage interne TRC)" />
                        <FormGroup className="mt-2"><Label>Note</Label><Input value={cell.note} onChange={(e) => setCell({ ...cell, note: e.target.value })} /></FormGroup>
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button color="secondary" onClick={() => setCell(null)}>Annuler</Button>
                    <Button color="primary" disabled={busy} onClick={saveCell}>{busy ? <Spinner size="sm" /> : "Enregistrer"}</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!season} toggle={() => setSeason(null)}>
                <ModalHeader toggle={() => setSeason(null)}>{season && `Saison — semaine du ${weekLabel(season.week)}`}</ModalHeader>
                {season && (
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        <FormGroup><Label>Prix appliqué à tous les espaces</Label>
                            <Input type="select" value={season.mode} onChange={(e) => setSeason({ ...season, mode: e.target.value })}>
                                <option value="default">Règle par défaut (haute saison en janvier, juin, novembre)</option>
                                <option value="high">Haute saison / temps fort</option>
                                <option value="low">Basse saison</option>
                            </Input></FormGroup>
                        <FormGroup><Label>Temps fort affiché (facultatif)</Label><Input value={season.label} maxLength={40} onChange={(e) => setSeason({ ...season, label: e.target.value })} placeholder="ex. French Days" /></FormGroup>
                        <small className="text-muted">Les partenaires voient le nom du temps fort dans leur calendrier. Les réservations déjà faites gardent leur prix.</small>
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button color="secondary" onClick={() => setSeason(null)}>Annuler</Button>
                    <Button color="primary" disabled={busy} onClick={saveSeason}>{busy ? <Spinner size="sm" /> : "Enregistrer"}</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!frame} toggle={() => setFrame(null)}>
                <ModalHeader toggle={() => setFrame(null)}>Réserver pour un partenaire</ModalHeader>
                {frame && (
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        {frame.result ? (
                            <>
                                <Alert color="success" className="mb-2">{frame.result.created.length} réservation(s) créée(s) : {frame.result.created.map((b) => b.weeks.map(weekLabel).join(", ")).join(" · ") || "aucune"}{frame.mode === "quote" && frame.result.created.length > 0 ? " — envoie le devis depuis Réservations." : ""}</Alert>
                                {frame.result.conflicts.length > 0 && <Alert color="warning">Semaines déjà prises, non réservées : {frame.result.conflicts.map(weekLabel).join(", ")}</Alert>}
                            </>
                        ) : (
                            <>
                                <FormGroup><Label>Espace</Label>
                                    <Input type="select" value={frame.spaceId} onChange={(e) => setFrame({ ...frame, spaceId: e.target.value, partner: "" })}>
                                        {rows.map((r) => <option key={r.space.id} value={r.space.id}>{r.space.name?.fr} ({r.space.market.toUpperCase()})</option>)}
                                    </Input></FormGroup>
                                <FormGroup><Label>Partenaire</Label>
                                    <Input type="select" value={frame.partner} onChange={(e) => setFrame({ ...frame, partner: e.target.value })}>
                                        <option value="">—</option>
                                        {framePartners.map((p) => <option key={`${p.merchantKey}#${p.market}`} value={`${p.merchantKey}#${p.market}`}>{p.displayName || p.merchantKey}{p.partnerType === "brand" ? " (marque)" : ""}</option>)}
                                    </Input></FormGroup>
                                <FormGroup><Label>Type</Label>
                                    <Input type="select" value={frame.mode} onChange={(e) => setFrame({ ...frame, mode: e.target.value })}>
                                        <option value="quote">Réservation classique — prix de la grille, devis Qonto à envoyer</option>
                                        <option value="direct">Sans devis — réservée d'office, réglée hors espace (ou offerte)</option>
                                        <option value="framework">Contrat cadre — inclus dans le contrat, sans devis</option>
                                    </Input></FormGroup>
                                {frameSpace?.unit && <FormGroup><Label>Nombre de {frameSpace.unit}s (max {frameSpace.maxQuantity})</Label><Input type="number" min={1} max={frameSpace.maxQuantity} value={frame.quantity} onChange={(e) => setFrame({ ...frame, quantity: e.target.value })} /></FormGroup>}
                                {frame.mode === "direct" && <>
                                    <FormGroup><Label>Référence (facultatif)</Label><Input value={frame.contractRef} onChange={(e) => setFrame({ ...frame, contractRef: e.target.value })} placeholder="ex. bon de commande, échange…" /></FormGroup>
                                    <FormGroup check className="mb-3"><Label check><Input type="checkbox" checked={!!frame.free} onChange={(e) => setFrame({ ...frame, free: e.target.checked })} /> Offerte (0 €)</Label></FormGroup>
                                </>}
                                {frame.mode === "framework" && <FormGroup><Label>Référence du contrat</Label><Input value={frame.contractRef} onChange={(e) => setFrame({ ...frame, contractRef: e.target.value })} placeholder="ex. CADRE-ALLTRICKS-2027" /></FormGroup>}
                                <div className="d-flex">
                                    <FormGroup className="mr-2"><Label>Du (semaine du lundi)</Label><Input type="date" value={frame.from} onChange={(e) => setFrame({ ...frame, from: mondayOf(new Date(e.target.value + "T12:00")) })} /></FormGroup>
                                    <FormGroup><Label>Au (dernière semaine, lundi)</Label><Input type="date" value={frame.to} onChange={(e) => setFrame({ ...frame, to: mondayOf(new Date(e.target.value + "T12:00")) })} /></FormGroup>
                                </div>
                                <small className="text-muted">{frame.mode === "framework"
                                    ? "Toutes les semaines de la période, acceptées d'office, sans devis ni facture (« Inclus dans votre contrat »)."
                                    : frame.mode === "direct"
                                        ? "Toutes les semaines de la période, réservées d'office : ni devis ni facture Qonto. Le partenaire peut déposer son contenu tout de suite."
                                        : "Toutes les semaines de la période, bloquées 7 jours en attente du devis — à envoyer depuis Réservations."}</small>
                            </>
                        )}
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button color="secondary" onClick={() => setFrame(null)}>{frame?.result ? "Fermer" : "Annuler"}</Button>
                    {!frame?.result && <Button color="primary" disabled={busy} onClick={saveFrame}>{busy ? <Spinner size="sm" /> : "Réserver"}</Button>}
                </ModalFooter>
            </Modal>
        </Container>
    );
}
